import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel,
  Button, Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Select, MenuItem, Alert, LinearProgress,
  Paper, TableContainer,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { importPhoneCatalog } from '../../services/phoneCatalogApi';

const STEPS = ['Upload', 'Column Mapping', 'Confirm', 'Result'];

const DB_FIELDS = [
  { key: 'provider', required: false },
  { key: 'country', required: true },
  { key: 'city', required: false },
  { key: 'code', required: false },
  { key: 'channels', required: false },
  { key: 'activation_fee', required: false },
  { key: 'monthly_fee', required: true },
  { key: 'internal_activation_fee', required: false },
  { key: 'internal_monthly_fee', required: false },
  { key: 'currency', required: false },
  { key: 'incoming_cost', required: false },
  { key: 'required_documents', required: false },
  { key: 'comment', required: false },
];

const FIELD_ALIASES = {
  provider: ['provider', 'оператор'],
  country: ['country', 'країна', 'страна'],
  city: ['city', 'місто', 'город', 'type'],
  code: ['code', 'код'],
  channels: ['channels', 'lines', 'канали', 'number of lines', 'number of lines for incoming'],
  activation_fee: ['activation fee', 'activation', 'підключення', 'activation fee for the customer'],
  monthly_fee: ['monthly fee', 'monthly', 'абонплата', 'monthly fee for the customer', 'monthly fee for the client'],
  currency: ['currency', 'валюта'],
  incoming_cost: ['incoming', 'incoming calls', 'incoming cost', 'incoming calls cost'],
  required_documents: ['documents', 'docs', 'документи', 'ordering data'],
  internal_activation_fee: ['internal activation', 'activation fee', 'собівартість активації'],
  internal_monthly_fee: ['internal monthly', 'monthly fee', 'собівартість'],
  comment: ['comment', 'коментар', 'notes', 'note'],
};

const FIELD_LABELS = {
  provider: 'Provider',
  country: 'Country',
  city: 'City / Type',
  code: 'Code',
  channels: 'Channels',
  activation_fee: 'Activation Fee (client)',
  monthly_fee: 'Monthly Fee (client)',
  internal_activation_fee: 'Activation Fee (internal)',
  internal_monthly_fee: 'Monthly Fee (internal)',
  currency: 'Currency',
  incoming_cost: 'Incoming Cost',
  required_documents: 'Required Documents',
  comment: 'Comment',
};

const CsvImportDialog = ({ open, onClose, onImportComplete }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const resetState = () => {
    setActiveStep(0);
    setIsDragging(false);
    setFileName('');
    setCsvData([]);
    setCsvHeaders([]);
    setMapping({});
    setImporting(false);
    setImportResult(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const autoDetectMapping = (headers) => {
    const detected = {};
    const usedIndices = new Set();

    // First pass: exact matches for non-internal fields (they have priority)
    DB_FIELDS.filter(({ key }) => !key.startsWith('internal_')).forEach(({ key }) => {
      const aliases = FIELD_ALIASES[key] || [];
      const matchIndex = headers.findIndex((h, i) =>
        !usedIndices.has(i) && aliases.some((alias) => h.toLowerCase().trim() === alias.toLowerCase())
      );
      if (matchIndex !== -1) {
        detected[key] = matchIndex;
        usedIndices.add(matchIndex);
      }
    });

    // Second pass: internal fields match remaining columns with same aliases
    DB_FIELDS.filter(({ key }) => key.startsWith('internal_')).forEach(({ key }) => {
      const aliases = FIELD_ALIASES[key] || [];
      const matchIndex = headers.findIndex((h, i) =>
        !usedIndices.has(i) && aliases.some((alias) => h.toLowerCase().trim() === alias.toLowerCase())
      );
      if (matchIndex !== -1) {
        detected[key] = matchIndex;
        usedIndices.add(matchIndex);
      }
    });

    return detected;
  };

  const parseFile = (file) => {
    Papa.parse(file, {
      complete: (results) => {
        const rows = results.data;
        if (rows.length < 2) return;

        const headers = rows[0].map((h) => String(h).trim());
        const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell && String(cell).trim()));

        setCsvHeaders(headers);
        setCsvData(dataRows);
        setFileName(file.name);
        setMapping(autoDetectMapping(headers));
        setActiveStep(1);
      },
      skipEmptyLines: true,
    });
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      parseFile(file);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const handleMappingChange = (field, colIndex) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (colIndex === '') {
        delete next[field];
      } else {
        next[field] = colIndex;
      }
      return next;
    });
  };

  const requiredFieldsMapped = DB_FIELDS
    .filter((f) => f.required)
    .every((f) => mapping[f.key] !== undefined);

  const handleImport = async () => {
    setImporting(true);
    try {
      const backendMapping = {};
      Object.entries(mapping).forEach(([field, colIndex]) => {
        backendMapping[field] = colIndex;
      });

      const response = await importPhoneCatalog(csvData, backendMapping);
      const result = response.data?.data || response.data || {};
      setImportResult({
        imported: result.imported || result.count || csvData.length,
        errors: result.errors || [],
      });
    } catch (err) {
      const errData = err.response?.data;
      setImportResult({
        imported: 0,
        errors: [{ message: errData?.error || err.message || 'Import failed' }],
      });
    } finally {
      setImporting(false);
      setActiveStep(3);
    }
  };

  // Step 0: Upload
  const renderUpload = () => (
    <Box sx={{ py: 3 }}>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? '#00C853' : '#e0e0e0',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragging ? 'rgba(0,200,83,0.04)' : 'transparent',
          transition: 'all 0.2s',
          '&:hover': { borderColor: '#00C853', bgcolor: 'rgba(0,200,83,0.04)' },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {t('csvImport.dropzone', 'Drag & drop CSV file here')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('csvImport.orClickToSelect', 'or click to select a file')}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileSelect}
        />
      </Box>
      {fileName && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success">
            {fileName} — {csvData.length} {t('csvImport.rows', 'rows')}
          </Alert>
        </Box>
      )}
    </Box>
  );

  // Step 1: Column Mapping
  const renderMapping = () => {
    const previewRows = csvData.slice(0, 5);

    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('csvImport.previewTitle', 'CSV Preview (first 5 rows)')}
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 240, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {csvHeaders.map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row, ri) => (
                <TableRow key={ri}>
                  {csvHeaders.map((_, ci) => (
                    <TableCell key={ci} sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                      {row[ci] || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t('csvImport.mappingTitle', 'Map CSV columns to database fields')}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, width: 200 }}>Database Field</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>CSV Column</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DB_FIELDS.map(({ key, required }) => (
              <TableRow key={key} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                    {FIELD_LABELS[key]}{required ? <Box component="span" sx={{ color: '#dc2626', ml: 0.5 }}>*</Box> : ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Select
                    size="small"
                    value={mapping[key] !== undefined ? mapping[key] : ''}
                    onChange={(e) => handleMappingChange(key, e.target.value)}
                    displayEmpty
                    fullWidth
                    sx={{ borderRadius: 1, fontSize: 13, height: 36 }}
                  >
                    <MenuItem value="">
                      <em style={{ color: '#9ca3af' }}>{t('csvImport.notMapped', '— Not mapped —')}</em>
                    </MenuItem>
                    {csvHeaders.map((h, i) => (
                      <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>
                        {h}
                        <Box component="span" sx={{ color: 'text.disabled', ml: 1 }}>col {i + 1}</Box>
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  // Step 2: Confirm
  const renderConfirm = () => (
    <Box sx={{ py: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('csvImport.confirmSummary', '{{count}} rows will be imported, replacing existing records.', {
          count: csvData.length,
        })}
      </Alert>
      <Alert severity="warning">
        {t('csvImport.confirmTopWarning', 'Top country flags will be preserved for matching countries.')}
      </Alert>
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {t('csvImport.mappingSummary', 'Column mapping:')}
        </Typography>
        {DB_FIELDS.map(({ key }) => {
          if (mapping[key] === undefined) return null;
          return (
            <Typography key={key} variant="body2" color="text.secondary">
              {FIELD_LABELS[key]} &larr; {csvHeaders[mapping[key]]}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );

  // Step 3: Result
  const renderResult = () => {
    if (!importResult) return null;
    const { imported, errors } = importResult;
    const hasErrors = errors && errors.length > 0;

    return (
      <Box sx={{ py: 3 }}>
        {imported > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('csvImport.importedCount', '{{count}} records imported successfully.', { count: imported })}
          </Alert>
        )}
        {hasErrors && (
          <>
            <Alert severity="error" sx={{ mb: 1 }}>
              {t('csvImport.errorsCount', '{{count}} error(s) occurred.', { count: errors.length })}
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
              {errors.slice(0, 5).map((err, i) => (
                <Typography key={i} variant="body2" color="error" sx={{ mb: 0.5 }}>
                  {err.row !== undefined ? `Row ${err.row}: ` : ''}{err.message || JSON.stringify(err)}
                </Typography>
              ))}
              {errors.length > 5 && (
                <Typography variant="body2" color="text.secondary">
                  ...{t('csvImport.andMoreErrors', 'and {{count}} more', { count: errors.length - 5 })}
                </Typography>
              )}
            </Paper>
          </>
        )}
        {!hasErrors && imported === 0 && (
          <Alert severity="warning">
            {t('csvImport.nothingImported', 'No records were imported.')}
          </Alert>
        )}
      </Box>
    );
  };

  const stepContent = [renderUpload, renderMapping, renderConfirm, renderResult];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 400 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('csvImport.title', 'Import CSV')}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{t(`csvImport.step.${label}`, label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {importing && <LinearProgress sx={{ mb: 2 }} />}
        {stepContent[activeStep]()}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {activeStep === 0 && (
          <Button onClick={handleClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t('common.back', 'Back')}
            </Button>
            <Button
              variant="contained"
              disabled={!requiredFieldsMapped}
              onClick={() => setActiveStep(2)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#00C853',
                '&:hover': { bgcolor: '#00A843' },
              }}
            >
              {t('common.next', 'Next')}
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t('common.back', 'Back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#00C853',
                '&:hover': { bgcolor: '#00A843' },
              }}
            >
              {t('csvImport.importButton', 'Import')}
            </Button>
          </>
        )}
        {activeStep === 3 && (
          <Button
            variant="contained"
            onClick={() => {
              if (importResult?.imported > 0) onImportComplete?.();
              handleClose();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#00C853',
              '&:hover': { bgcolor: '#00A843' },
            }}
          >
            {t('common.close', 'Close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportDialog;
